// kaiContext.js — the ONE shared context builder for every Kai path.
//
// Coach chat (api/coach.js), the morning briefing (src/Scorify.jsx -> api/claude.js),
// and notify-cron (api/lib/coach-run.js) all consume this module so that COVERAGE
// (which domains Kai sees) and SHAPING (what data, which freshness window, which
// computed values) stop diverging. src/lib is imported directly by the client AND
import { weekDays, localDateInZone } from './weekWindow.js';
// the server — same convention as screenTimeUsage.js / freshness.js / bills.js.
//
// Design notes:
// - Shapers are the shared "builder". Each takes NORMALIZED inputs (not raw client
//   state or raw DB rows) and returns a structured section with every computed
//   value + freshness label already applied. Formatting to text stays per-path
//   (chat renders multi-line blocks, cron one-liners), because each path's prompt
//   is tuned to its own format; unifying the DATA, not the prose, is the goal.
// - Two normalizers map each origin into the shared input shape:
//     normalizeClientState(state)  — chat + briefing (React state)
//     normalizeServerBundle(bundle, profile) — cron (DB rows)
//   Keeping both pure and node-importable lets the coverage-drift test assert
//   every domain survives each adapter, and makes a future server-side briefing a
//   small change (swap the adapter, keep the shapers).
// - Goals and bills arrive already resolved/annotated (goal-progress.js /
//   bills.js are the existing shared resolvers); their shapers are pass-throughs
//   that only standardize the field shape.

import { reconstructScreenTimeUsage, formatUsageMinutes } from './screenTimeUsage.js';

// Canonical domain registry. The coverage-drift test asserts every path surfaces
// each of these. Adding a domain here forces it into both normalizers (or the
// test fails), so a source added to one path cannot silently skip another.
// CONNECTING THE DOTS (reworked 2026-10-01): connecting a user's life across domains
// is the core of the product, so Kai should do it actively — but with confidence
// matched to evidence, and never in causal/certain language. Shared verbatim by every
// prompt path (chat + all /api/coach modes, the briefing, and notify-cron) so the rule
// reads identically everywhere and can be unit tested.
export const PATTERN_HONESTY_RULE = `CONNECTING THE DOTS (this is the core of coaching: do it whenever relevant, across mood, journal, sleep, workouts, habits, spending, screen time, and commitments. Match your confidence to your evidence):
- OBSERVATIONS (no confirmed finding yet): connect freely as an observation or a gentle hypothesis, grounded in the SPECIFIC facts you can see: "your two highest mood days this week were both Muay Thai days, worth watching," or "spending crept up the week the gym went quiet." Name the facts and frame it as something you noticed, not a proven rule.
- CONFIRMED FINDINGS (given to you in a findings or patterns block): state the tendency with its numbers where the surface allows specifics (chat, the in-app briefing, the check-in body): "on Muay Thai days your mood averages 9.6 vs 8.1." On a lock-screen headline, name the topic without the numbers.
- NEVER causal or certain, with OR without a finding: do not say "clearly", "fueling", "causes", "is why", "because of", "driving", or "due to". Use "tends to", "lines up with", "the days you did X, Y was higher" — put the two facts side by side and let the user connect them.
- THEIR OWN WORDS: you may echo a connection the user themselves drew in a journal entry or mood note ("you wrote that Muay Thai left you feeling great") when journal sharing is on. Never quote journal content in a lock-screen notification.
- SENSITIVE DOMAINS (mood, mental health, spending, weight): observation only, never blame or a verdict — "I noticed," never "you always" or "you overspend because."`;

// Causal/certain phrasing that is never allowed when connecting the dots (the rule
// above bans these). Returns the offending phrases found in a piece of text.
const CAUSAL_PATTERNS = [/\bclearly\b/i, /\bfuel(?:s|ing|ed)?\b/i, /\bcaus(?:e|es|ed|ing)\b/i, /\bis why\b/i, /\bbecause of\b/i, /\bdriv(?:es|ing|en)\b/i, /\bdue to\b/i, /\bthanks to\b/i];
export function findCausalLanguage(text) {
  const t = String(text || '');
  return CAUSAL_PATTERNS.map((re) => (t.match(re) || [])[0]).filter(Boolean);
}

export const KAI_DOMAINS = [
  'pillars', 'habits', 'supplements', 'medications', 'recovery', 'progress',
  'mood', 'mentalHealth', 'journal', 'sleep', 'activity', 'screenTime',
  'finance', 'debts', 'bills', 'goals', 'focus', 'body', 'weight', 'scoreHistory',
  'findings', 'commitments',
];

const MS_DAY = 86400000;
const HEALTH_SYNC_STALE_H = 24;   // activity "as of last sync" past this
const MOOD_STALE_DAYS = 3;        // mood average labeled stale past this

// ── small shared helpers ────────────────────────────────────────────────────
export const milesFromMeters = (m) => (Number(m || 0) / 1609.34);
export const fmtSleepH = (min) => {
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};
// Bedtime as minutes past 18:00 local (mod 24h) so 11pm..2am is a monotonic
// range and a median is meaningful. Matched across the cron, the chat, and
// Health.jsx — one implementation here now.
export function bedtimeMinutes(iso, zone) {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone || 'UTC', hour12: false, hour: '2-digit', minute: '2-digit' })
    .formatToParts(new Date(iso));
  const h = Number(parts.find(p => p.type === 'hour')?.value || 0) % 24;
  const m = Number(parts.find(p => p.type === 'minute')?.value || 0);
  return (h * 60 + m + 1440 - 18 * 60) % 1440;
}
export function bedtimeClock(minPast18) {
  const total = (18 * 60 + minPast18) % 1440;
  const h = Math.floor(total / 60), m = total % 60;
  return `${h % 12 || 12}:${String(m).padStart(2, '0')}${h >= 12 ? 'pm' : 'am'}`;
}
const daysBetweenLocalDates = (fromYmd, toYmd) => {
  if (!fromYmd || !toYmd) return null;
  const a = new Date(`${String(fromYmd).slice(0, 10)}T12:00:00`);
  const b = new Date(`${String(toYmd).slice(0, 10)}T12:00:00`);
  return Math.round((b - a) / MS_DAY);
};

// ── MOOD ────────────────────────────────────────────────────────────────────
// moods: [{ score, date, note? }] ascending (oldest -> newest). note is already
// consent-gated upstream (journal_settings.coach_enabled); this shaper never
// un-gates it. recentAvg over the last up-to-7 scored entries.
export function shapeMood(moods, { today, nowMs } = {}) {
  const valid = (moods || []).filter(m => m && m.score != null);
  if (!valid.length) return { available: false };
  // "Last 7 days" means the last 7 LOCAL DAYS of entries (today and the six
  // before), not the last 7 entries; the prior week is the 7 days before
  // that (2026-10-15: chat averaged entries, the check-in averaged days, and
  // two logs on one day skewed one of them). Without `today` (no clock
  // given), the entry-based window is kept.
  const dayOf = (m) => (m.date ? String(m.date).slice(0, 10) : null);
  const ago = (m) => { const d = dayOf(m); return d && today ? daysBetweenLocalDates(d, today) : null; };
  const inDays = (m, lo, hi) => { const n = ago(m); return n != null && n >= lo && n <= hi; };
  const windowed = !!today && valid.some(m => ago(m) != null);
  const last7Days = windowed ? valid.filter(m => inDays(m, 0, 6)) : [];
  // No entry in the last 7 days: fall back to the newest entries so the
  // average still exists; `stale`/`asOf` below label how old they are.
  const last7 = last7Days.length ? last7Days : valid.slice(-7);
  const prev7 = windowed && last7Days.length ? valid.filter(m => inDays(m, 7, 13)) : valid.slice(-14, -7);
  const avg = +(last7.reduce((s, m) => s + m.score, 0) / last7.length).toFixed(1);
  const prevAvg = prev7.length ? +(prev7.reduce((s, m) => s + m.score, 0) / prev7.length).toFixed(1) : null;
  const newest = valid[valid.length - 1];
  const newestDate = newest.date ? String(newest.date).slice(0, 10) : null;
  const daysAgo = today ? daysBetweenLocalDates(newestDate, today)
    : (newestDate && nowMs ? Math.floor((nowMs - new Date(`${newestDate}T12:00:00`).getTime()) / MS_DAY) : null);
  const stale = daysAgo != null && daysAgo >= MOOD_STALE_DAYS;
  return {
    available: true,
    recentAvg: avg,
    prevAvg,
    trend: prevAvg != null ? +(avg - prevAvg).toFixed(1) : null,
    last7Scores: last7.map(m => m.score),
    recent14: valid.slice(-14).map(m => ({ score: m.score, date: m.date ? String(m.date).slice(0, 10) : null, note: m.note || null })),
    loggedToday: today ? valid.some(m => String(m.date).slice(0, 10) === today) : null,
    newestDate, daysAgo, stale,
    asOf: stale ? ` (last logged ${newestDate}, ${daysAgo} days ago)` : '',
  };
}

// ── ACTIVITY (steps + workouts) ──────────────────────────────────────────────
// dailyActivity: [{ date, steps, distanceM }], workouts: [{ type, date, distanceM }].
// Trailing-7-day window = today plus previous six LOCAL days (parity with every
// surface; the -6 off-by-one that caused the 8-day "7-day" total). healthLastSyncAt
// drives the "as of last sync" stamp past 24h (HealthKit only syncs foregrounded).
export function shapeActivity(dailyActivity, workouts, { today, nowMs, healthLastSyncAt } = {}) {
  const da = dailyActivity || [], wo = workouts || [];
  const available = da.length > 0 || wo.length > 0;
  const weekCut = today
    ? new Date(new Date(`${today}T12:00:00Z`).getTime() - 6 * MS_DAY).toISOString().slice(0, 10)
    : null;
  const last7 = weekCut ? da.filter(d => d.date && d.date >= weekCut) : da;
  const steps7d = last7.reduce((s, d) => s + (d.steps || 0), 0);
  const meters7d = last7.reduce((s, d) => s + (d.distanceM || 0), 0);
  const workouts7d = weekCut ? wo.filter(w => w.date && w.date >= weekCut).length : wo.length;
  const syncAgeH = (healthLastSyncAt && nowMs) ? (nowMs - new Date(healthLastSyncAt).getTime()) / 3600000 : null;
  const stale = syncAgeH != null && syncAgeH > HEALTH_SYNC_STALE_H;
  return {
    available,
    everSynced: !!healthLastSyncAt,
    steps7d,
    miles7d: +milesFromMeters(meters7d).toFixed(1),
    avgSteps7d: last7.length ? Math.round(steps7d / last7.length) : 0,
    workouts7d,
    perDay: da.slice(0, 14).map(d => ({ date: d.date, steps: d.steps || 0, miles: +milesFromMeters(d.distanceM).toFixed(1) })),
    // Minutes and the user's note ride along (2026-10-23) under the AI consent
    // (not the journal toggle); notes never reach the lock screen (journal-quote guard).
    workoutList: wo.slice(0, 20).map(w => ({ type: w.type, date: w.date, miles: w.distanceM ? +milesFromMeters(w.distanceM).toFixed(2) : null,
      minutes: Number(w.durationMin) > 0 ? Number(w.durationMin) : null, note: typeof w.note === 'string' && w.note.trim() ? w.note.trim() : null })),
    stale,
    asOf: stale ? ` (as of last sync ${String(healthLastSyncAt).slice(0, 10)}, may be missing recent days)` : '',
  };
}

// ── SLEEP ────────────────────────────────────────────────────────────────────
// sleepNights: [{ date, asleepMin, inBedMin, sleepStartAt }] desc. The inBed
// HONESTY RULE: asleepMin null = schedule artifact (alarm settings), NOT sleep.
export function shapeSleep(sleepNights, { zone } = {}) {
  const nights = sleepNights || [];
  const measured = nights.filter(n => n.asleepMin != null);
  if (measured.length > 0) {
    const n = Math.min(measured.length, 7);
    const avgMin = measured.slice(0, n).reduce((s, x) => s + x.asleepMin, 0) / n;
    const bedMins = measured.map(x => bedtimeMinutes(x.sleepStartAt, zone)).sort((a, b) => a - b);
    return {
      status: 'measured',
      available: true,
      nightsCount: n,
      avgMin,
      avgLabel: fmtSleepH(avgMin),
      typicalBedtime: bedtimeClock(bedMins[Math.floor(bedMins.length / 2)]),
      nights: measured.map(x => ({ date: x.date, asleepLabel: fmtSleepH(x.asleepMin), bedtime: bedtimeClock(bedtimeMinutes(x.sleepStartAt, zone)) })),
    };
  }
  if (nights.length > 0) return { status: 'schedule-only', available: false };
  return { status: 'none', available: false };
}

// ── SCREEN TIME ───────────────────────────────────────────────────────────────
// screenTimeDays: raw day rows (reconstructScreenTimeUsage consumes the row for
// `today`). Stepped floor; never an exact total.
export function shapeScreenTime(screenTimeDays, { today } = {}) {
  const row = (screenTimeDays || []).find(d => d.date === today);
  const usage = reconstructScreenTimeUsage(row);
  if (!usage) return { available: false };
  return { available: true, minutes: usage.minutes, label: formatUsageMinutes(usage.minutes), asOfLabel: usage.asOfLabel };
}

// ── RECOVERY STREAKS ──────────────────────────────────────────────────────────
// recovery: [{ label, startDate }] -> days clean = days since startDate.
export function shapeRecovery(recovery, { today } = {}) {
  const items = (recovery || []).map(r => ({
    label: r.label,
    days: daysBetweenLocalDates(r.startDate, today),
    // A start date after `today` is a tracker not yet started (or a replayed day
    // before a reset): "-3d clean" reached a recap on 2026-10-11. Never shown.
  })).filter(r => r.label && Number.isFinite(r.days) && r.days >= 0);
  return { available: items.length > 0, items };
}

// ── PROGRESS (XP / level / badges) ─────────────────────────────────────────────
export function shapeProgress(progress) {
  if (!progress) return { available: false };
  return {
    available: true,
    xp: Number(progress.xp || 0),
    level: progress.level || null,
    dailyStreak: Number(progress.dailyStreak || 0),
    longestStreak: Number(progress.longestStreak || 0),
    badges: Array.isArray(progress.badges) ? progress.badges : [],
  };
}

// ── HABITS ──────────────────────────────────────────────────────────────────
// habits: [{ name, label, streak, weekCount, target, unit, loggedToday, ageDays, noteToday }]
// noteToday: the user's own note on today's log (journal consent only; null otherwise).
// label/target/unit are the ROW's saved values (an edited "4 times/week" lives
// there, template or custom); consumers print `label || name` and count
// weekCount against target, never against a hardcoded template.
export function shapeHabits(habits) {
  const items = (habits || []).map(h => ({
    name: h.name, label: h.label || null, streak: h.streak || 0, weekCount: h.weekCount ?? null,
    target: h.target ?? null, unit: h.unit || null, loggedToday: !!h.loggedToday,
    isNew: Number.isFinite(h.ageDays) && h.ageDays <= 1, ageDays: h.ageDays ?? null,
    noteToday: typeof h.noteToday === 'string' && h.noteToday.trim() ? h.noteToday.trim() : null,
  }));
  return {
    available: items.length > 0, items,
    doneToday: items.filter(h => h.loggedToday).length, total: items.length,
    atRisk: items.filter(h => h.streak > 2 && !h.loggedToday),
    strong: items.filter(h => h.streak >= 7),
  };
}

// ── SUPPLEMENTS ───────────────────────────────────────────────────────────────
export function shapeSupplements(supplements) {
  const items = (supplements || []).map(s => ({ name: s.name, streak: s.streak || 0, takenToday: !!s.takenToday }));
  return { available: items.length > 0, items, doneToday: items.filter(s => s.takenToday).length, total: items.length };
}

// ── MEDICATIONS ───────────────────────────────────────────────────────────────
// medications: [{ name, schedule, lastTakenDate }]. "As needed" meds are never
// "due". dueToday = scheduled meds not logged on `today`.
export function shapeMedications(medications, { today } = {}) {
  const asNeeded = /as.?needed|prn|when needed/i;
  const items = (medications || []).map(m => ({
    name: m.name, schedule: m.schedule || null,
    takenToday: m.lastTakenDate ? String(m.lastTakenDate).slice(0, 10) === today : false,
    scheduled: !asNeeded.test(m.schedule || ''),
  }));
  const dueToday = items.filter(m => m.scheduled && !m.takenToday).map(m => m.name);
  return { available: items.length > 0, items, dueToday, missedToday: dueToday.length > 0 };
}

// ── FINANCE ───────────────────────────────────────────────────────────────────
// Pass-through of already-derived finance figures (each path derives these with
// its own matched-pair logic + the shared freshness window). This shaper only
// standardizes the shape; it does not re-derive balances.
export function shapeFinance(finance) {
  if (!finance) return { available: false };
  return { available: true, ...finance };
}

// ── DEBTS ─────────────────────────────────────────────────────────────────────
// debts: [{ name, balance, rate, monthlyPayment }]
export function shapeDebts(debts, { income } = {}) {
  const items = (debts || []).map(d => ({ name: d.name, balance: d.balance, rate: d.rate ?? null, monthlyPayment: d.monthlyPayment ?? null }));
  if (!items.length) return { available: false, items: [] };
  const known = items.filter(d => Number.isFinite(d.balance));
  const totalKnown = known.reduce((a, d) => a + d.balance, 0);
  const payments = items.map(d => Number(d.monthlyPayment));
  const allPaymentsKnown = payments.length > 0 && payments.every(p => Number.isFinite(p) && p >= 0);
  const totalPayments = allPaymentsKnown ? payments.reduce((a, p) => a + p, 0) : 0;
  const dti = (allPaymentsKnown && income > 0 && totalPayments > 0) ? Math.round((totalPayments / income) * 100) : null;
  return {
    available: true, items,
    totalKnown, unknownCount: items.length - known.length,
    dti, totalPayments,
    highInterest: items.filter(d => parseFloat(d.rate) > 15),
  };
}

// ── GOALS / BILLS (pass-through of already-resolved/annotated data) ─────────────
export function shapeGoals(resolvedGoalLines) {
  const lines = resolvedGoalLines || [];
  return { available: lines.length > 0, lines };
}
export function shapeBills(annotatedBills) {
  const bills = annotatedBills || [];
  return { available: bills.length > 0, bills };
}

// ── simple pass-through sections ──────────────────────────────────────────────
export function shapeMentalHealth(mh) {
  if (!mh || mh.phqTotal == null) return { available: false };
  return { available: true, phqTotal: mh.phqTotal, gadTotal: mh.gadTotal };
}
export function shapeJournal(entries) {
  const items = (entries || []).slice(0, 5).map(e => ({ date: e.date, text: String(e.text || '').slice(0, 200), moodScore: e.moodScore ?? null }));
  return { available: items.length > 0, items };
}
export function shapeFocus(profile) {
  if (!profile?.focusPillar) return { available: false };
  return { available: true, pillar: profile.focusPillar, note: profile.focusNote ? String(profile.focusNote).slice(0, 280) : null };
}
export function shapeBody(bodyStats) {
  if (!bodyStats?.currentWeight) return { available: false };
  return { available: true, currentWeight: bodyStats.currentWeight, goalWeight: bodyStats.goalWeight ?? null, goalType: bodyStats.goalType || null };
}
export function shapeWeight(weightLog) {
  const wl = (weightLog || []).slice(-7);
  return { available: wl.length > 0, entries: wl.map(w => w.weight) };
}
export function shapeScoreHistory(scoreHistory) {
  if (!scoreHistory || typeof scoreHistory !== 'object') return { available: false };
  const series = Object.entries(scoreHistory).map(([pillar, rows]) => ({ pillar, pcts: (rows || []).map(r => r.pct) }));
  return { available: series.length > 0, series };
}
export function shapePillars(pillars) {
  if (!pillars || typeof pillars !== 'object') return { available: false };
  return { available: true, pcts: pillars };
}

// ── FINDINGS (Phase 3 correlation findings from pattern_insights, ledger:* keys) ──
// Pass-through + surface-gating of findings the correlation pass stored. Only
// cleared (strength >= 0.5), recently re-detected (within ~8 days of the daily
// cron) findings surface; mood findings honor the user's mood-patterns setting.
// The evidence carries numbers — fine for chat and the in-app briefing (behind the
// app lock), NOT the lock-screen headline (CHECKIN_PRIVACY still bans numbers there).
const FINDING_RECENT_MS = 8 * 86400000;
const FINDING_MIN_STRENGTH = 0.5;
// EVIDENCE FLOOR (2026-10-19): a finding is surfaced anywhere only when it
// rests on at least this many paired days with this many days in each group
// (the same floor api/lib/correlations.js now stores at). Rows written before
// the floor was raised are gated here too, so a 16-day tendency stops being
// quoted the day this ships, not when the ledger cron next runs. Mutable on
// purpose: the replay's baseline arm sets the old 14/4 to reproduce history.
export const FINDING_FLOOR = { paired: 21, group: 6 };
export function shapeFindings(findings, { nowMs, moodPatternsEnabled = true } = {}) {
  const items = (findings || []).filter(f => {
    if (!f || typeof f.pattern_key !== 'string' || !f.pattern_key.startsWith('ledger:')) return false;
    if (Number(f.strength) < FINDING_MIN_STRENGTH) return false;
    const n = Number(f.snapshot?.sample_size), lo = Number(f.snapshot?.low_n), hi = Number(f.snapshot?.high_n);
    if (!(n >= FINDING_FLOOR.paired) || !(lo >= FINDING_FLOOR.group) || !(hi >= FINDING_FLOOR.group)) return false;
    if (f.involves_mood && !moodPatternsEnabled) return false;
    const ld = f.last_detected_at ? new Date(f.last_detected_at).getTime() : 0;
    if (nowMs && ld && (nowMs - ld) > FINDING_RECENT_MS) return false;
    return true;
  }).map(f => ({
    key: f.pattern_key,
    direction: f.direction,
    strength: Number(f.strength),
    confidence: f.snapshot?.confidence || 'tentative',
    evidence: f.snapshot?.evidence || null,
    involvesMood: !!f.involves_mood,
    sampleSize: f.snapshot?.sample_size ?? null,
    dateRange: f.snapshot?.date_range || null,
  })).sort((a, b) => b.strength - a.strength);
  return { available: items.length > 0, items };
}

// ── COMMITMENTS (Phase 4 coach_commitments) ───────────────────────────────────
// Open commitments and just-resolved ones (done within ~2 days), for Kai to follow
// up on and acknowledge. The novelty/no-nag rules live in the prompts; this shaper
// just surfaces the rows and marks how long each has been open.
const JUST_RESOLVED_MS = 2 * 86400000;
export function shapeCommitments(commitments, { nowMs, today } = {}) {
  const all = Array.isArray(commitments) ? commitments : [];
  const daysOpen = (c) => {
    const created = c.created_at ? new Date(c.created_at).getTime() : null;
    return created && nowMs ? Math.floor((nowMs - created) / 86400000) : null;
  };
  const open = all.filter(c => c.status === 'open').map(c => ({
    text: c.text, dueDate: c.due_date || null, source: c.source,
    daysOpen: daysOpen(c),
    overdue: !!(c.due_date && today && String(c.due_date).slice(0, 10) < today),
  }));
  const justResolved = all.filter(c => c.status === 'done' && c.resolved_at
    && nowMs && (nowMs - new Date(c.resolved_at).getTime()) <= JUST_RESOLVED_MS)
    .map(c => ({ text: c.text, evidence: c.evidence || null }));
  return { available: open.length > 0 || justResolved.length > 0, open, justResolved };
}

// ── BUILD ─────────────────────────────────────────────────────────────────────
// inputs: normalized shape (see normalizers below). Returns a structured context,
// one section per KAI_DOMAIN, every section carrying { available } at minimum.
export function buildKaiContext(inputs) {
  const i = inputs || {};
  const ctx = {
    pillars: shapePillars(i.pillars),
    habits: shapeHabits(i.habits),
    supplements: shapeSupplements(i.supplements),
    medications: shapeMedications(i.medications, i),
    recovery: shapeRecovery(i.recovery, i),
    progress: shapeProgress(i.progress),
    mood: shapeMood(i.moods, i),
    mentalHealth: shapeMentalHealth(i.mentalHealth),
    journal: shapeJournal(i.journalEntries),
    sleep: shapeSleep(i.sleepNights, i),
    activity: shapeActivity(i.dailyActivity, i.workouts, i),
    screenTime: shapeScreenTime(i.screenTimeDays, i),
    finance: shapeFinance(i.finance),
    debts: shapeDebts(i.debts, { income: i.finance?.income ?? i.finance?.monthlyIncome }),
    bills: shapeBills(i.bills),
    goals: shapeGoals(i.goalLines),
    focus: shapeFocus(i.profile),
    body: shapeBody(i.bodyStats),
    weight: shapeWeight(i.weightLog),
    scoreHistory: shapeScoreHistory(i.scoreHistory),
    findings: shapeFindings(i.findings, { nowMs: i.nowMs, moodPatternsEnabled: i.moodPatternsEnabled !== false }),
    commitments: shapeCommitments(i.commitments, { nowMs: i.nowMs, today: i.today }),
  };
  return ctx;
}

// ── NORMALIZERS ───────────────────────────────────────────────────────────────
// Map each origin into the shared input shape. Pure + node-importable so the
// coverage-drift test can assert every KAI_DOMAIN survives each adapter. Each
// returns the SAME keys; a path that cannot supply a domain passes [] / null,
// which the shaper renders as { available: false } (a valid "unknown" state,
// never a zero).

// Chat + briefing (React state). `state` carries already-derived client values.
export function normalizeClientState(state = {}) {
  return {
    today: state.today, nowMs: state.nowMs, zone: state.zone,
    healthLastSyncAt: state.healthLastSyncAt || null,
    findings: state.findings || [],
    commitments: state.commitments || [],
    moodPatternsEnabled: state.moodPatternsEnabled !== false,
    profile: state.profile || null,
    pillars: state.pillars || null,
    habits: state.habits || [],
    supplements: state.supplements || [],
    medications: state.medications || [],
    recovery: state.recovery || [],
    progress: state.progress || null,
    moods: state.moods || [],
    mentalHealth: state.mentalHealth || null,
    journalEntries: state.journalEntries || [],
    sleepNights: state.sleepNights || [],
    dailyActivity: state.dailyActivity || [],
    workouts: state.workouts || [],
    screenTimeDays: state.screenTimeDays || [],
    finance: state.finance || null,
    debts: state.debts || [],
    bills: state.bills || [],
    goalLines: state.goalLines || [],
    bodyStats: state.bodyStats || null,
    weightLog: state.weightLog || [],
    scoreHistory: state.scoreHistory || null,
  };
}

// Logs in the current local week, Monday-start, from a habit's history (JSON
// string or array of 'YYYY-MM-DD'). Same week boundary as the client's
// startOfWeek in Scorify.jsx (saveHabit / loadData), so the cron's
// "weekCount/target" matches the card. Null when today is unknown.
export function habitWeekCount(history, todayStr) {
  if (!todayStr) return null;
  let hist = history;
  if (typeof hist === 'string') { try { hist = JSON.parse(hist); } catch { hist = []; } }
  if (!Array.isArray(hist)) return 0;
  // THE week definition (src/lib/weekWindow.js): Monday-start, local dates.
  return weekDays(hist, todayStr).size;
}

// Cron (DB rows from fetchUserBundle + the profile row). snake_case -> camelCase.
export function normalizeServerBundle(bundle = {}, profile = {}, extras = {}) {
  return {
    today: extras.today, nowMs: extras.nowMs, zone: extras.zone,
    healthLastSyncAt: profile.health_last_sync_at || null,
    findings: extras.findings || [],
    commitments: extras.commitments || [],
    moodPatternsEnabled: profile.mood_patterns_enabled !== false,
    profile: { focusPillar: profile.focus_pillar || null, focusNote: profile.focus_note || null },
    pillars: extras.pillars || null,
    // last_completed / last_taken_date -> loggedToday/takenToday here: the cron
    // deliberately does NOT select the sticky `completed`/`taken_today` columns
    // (they are never reset), so the live answer is derived from the date, same
    // rule as loggedOnLocalDate in coach-run.js. label/target/unit come from
    // the row (saveEditHabit writes them; coach-run selects them) so the
    // check-in counts against the SAVED target, same as the client's tmpl().
    // weekCount is derived from history over the current local Mon-start week,
    // the same week the client's startOfWeek uses. ageDays stays client-only.
    habits: (bundle.habits || []).map(h => ({
      name: h.name, label: h.label || null,
      streak: h.streak,
      weekCount: habitWeekCount(h.history, extras.today),
      target: Number(h.target) > 0 ? Number(h.target) : null,
      unit: h.unit || null,
      loggedToday: h.last_completed ? String(h.last_completed).slice(0, 10) === extras.today : false,
      ageDays: null,
      // Habit notes ride with journal consent (extras.habitNotes = journalCoachEnabled).
      noteToday: extras.habitNotes && h.notes && typeof h.notes === 'object' && typeof h.notes[extras.today] === 'string' ? h.notes[extras.today] : null,
    })),
    supplements: (bundle.supplements || []).map(s => ({ name: s.name, streak: s.streak, takenToday: s.last_taken_date ? String(s.last_taken_date).slice(0, 10) === extras.today : false })),
    medications: (bundle.medications || []).map(m => ({ name: m.name, schedule: m.schedule, lastTakenDate: m.last_taken_date })),
    recovery: (bundle.recoveryTrackers || []).map(r => ({ label: r.label, startDate: r.start_date })),
    progress: bundle.progress ? { xp: bundle.progress.xp, level: bundle.progress.level, dailyStreak: bundle.progress.daily_streak, longestStreak: bundle.progress.longest_streak, badges: bundle.progress.badges } : null,
    // cron moods are {score, logged_at} desc -> {score, date, note} ascending.
    // LOCAL date of the instant (zone given): the UTC slice made a 9:44 PM
    // Pacific entry "today" the next morning (2026-10-15).
    moods: (bundle.moods || []).map(m => ({ score: m.score, date: m.logged_at ? (extras.zone ? localDateInZone(Date.parse(m.logged_at), extras.zone) : String(m.logged_at).slice(0, 10)) : null, note: null })).reverse(),
    mentalHealth: extras.mentalHealth || null,
    journalEntries: extras.journalEntries || [],
    sleepNights: (bundle.sleepNights || []).map(n => ({ date: n.date, asleepMin: n.asleep_min, inBedMin: n.in_bed_min, sleepStartAt: n.sleep_start_at })),
    dailyActivity: (bundle.dailyActivity || []).map(d => ({ date: d.date, steps: d.steps, distanceM: d.distance_m })),
    workouts: (bundle.workouts || []).map(w => ({ type: w.type, date: w.date, distanceM: w.distance_m , durationMin: w.duration_min ?? null, note: typeof w.notes === 'string' && w.notes.trim() ? w.notes.trim() : null })),
    screenTimeDays: bundle.screenTimeDays || [],
    finance: extras.finance || null,
    debts: extras.debts || [],
    bills: extras.bills || [],
    goalLines: extras.goalLines || [],
    bodyStats: extras.bodyStats || null,
    weightLog: extras.weightLog || [],
    scoreHistory: extras.scoreHistory || null,
  };
}

// ── WORKOUT LINES (2026-10-23) ───────────────────────────────────────────────
// "Thu 10-22: Sport / Martial arts, 45 min, 3.1 mi, note: "Muay Thai"" for the
// check-in's STANDING block; newest first, `limit` rows, within `days` of today.
const WD3 = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export function workoutLines(workoutList = [], { today, days = 7, limit = 5 } = {}) {
  const cut = today ? new Date(new Date(`${today}T12:00:00Z`).getTime() - (days - 1) * MS_DAY).toISOString().slice(0, 10) : '0000-00-00';
  return (workoutList || [])
    .filter(w => w && w.date && w.date >= cut && (!today || w.date <= today))
    .sort((a, b) => String(b.date).localeCompare(String(a.date)))
    .slice(0, limit)
    .map(w => {
      const d = new Date(`${String(w.date).slice(0, 10)}T12:00:00Z`);
      const day = `${WD3[d.getUTCDay()]} ${String(w.date).slice(5, 10)}`;
      return `${day}: ${[w.type || 'workout', w.minutes ? `${w.minutes} min` : null, w.miles ? `${w.miles} mi` : null, w.note ? `note: "${w.note}"` : null].filter(Boolean).join(', ')}`;
    });
}
